import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { PlateDecodeFields, VinDecodeFields } from "@autorepair/contracts";
import { ApiError, customersApi, vehicleReferenceApi, vehiclesApi, tenantSettingsApi } from "../lib/api";
import { composeEngineDescription, VIN_FIELD_LABELS } from "../lib/vinDecode";
import { PLATE_DECODER_ENABLED, PLATE_FIELD_LABELS, US_STATE_CODES } from "../lib/plateDecode";
import { useConfirmGate } from "../lib/useConfirm";
import { Spinner } from "../components/ui-modern";
import {
  ModernBreadcrumbs,
  ModernButton,
  ModernCard,
  ModernConfirmDialog,
  ModernErrorBanner,
  ModernField,
  ModernInput,
  ModernSelect,
} from "../components/ui-modern";

/** Sentinel option value for "none of these — let me type it in" — matches
 * VehicleForm.tsx's own OTHER sentinel exactly (a real catalog id is always
 * a positive number, never this string). */
const OTHER = "__other__";

/**
 * The modern theme's Add Vehicle page — see docs/FRONTEND_BACKLOG.md
 * Round 62. `VehicleForm.tsx` (shared by this page and EditVehiclePage.tsx)
 * is styled entirely off `ui.tsx`'s classic primitives and out of scope to
 * touch this round — so this inlines the SAME logic (VIN decode, the
 * Year/Make/Model/Trim catalog picker with its manual-fallback rules, and
 * the license-plate decoder, gated by the same `PLATE_DECODER_ENABLED`
 * flag — see that flag's own doc comment on why it stays fully built but
 * off) restyled with ui-modern.tsx's primitives, same tradeoff every other
 * Modern screen makes (thin re-run of the same calls/logic, not a shared
 * hook/component). See NewCustomerPageModern.tsx's own note on why the
 * classic Input's forced-uppercase-while-typing isn't replicated here
 * either — except the VIN/plate fields' own explicit `.toUpperCase()`
 * calls, which are real form logic (not the Input primitive's cosmetic
 * quirk) and ARE preserved verbatim below.
 */
export function NewVehiclePageModern() {
  const { customerNumber: customerNumberParam } = useParams();
  const customerNumber = Number(customerNumberParam);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo");
  const fromWorkOrder = returnTo === "work-order";

  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ["customer", "by-number", customerNumber],
    queryFn: () => customersApi.getByNumber(customerNumber),
  });
  const id = data?.customer.id as number;
  const backToWorkOrder = `/work-orders/new?customerId=${id}`;

  const [vin, setVin] = useState("");
  const [modelYear, setModelYear] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [trim, setTrim] = useState("");
  const [engineDescription, setEngineDescription] = useState("");
  const [licensePlate, setLicensePlate] = useState("");
  const [licensePlateState, setLicensePlateState] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [lastOdometer, setLastOdometer] = useState("");

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [decoding, setDecoding] = useState(false);
  const [decodeNotice, setDecodeNotice] = useState<string | null>(null);
  const [decodedFields, setDecodedFields] = useState<VinDecodeFields | null>(null);
  const [plateState, setPlateState] = useState("TX");
  const [decodingPlate, setDecodingPlate] = useState(false);
  const [plateDecodeNotice, setPlateDecodeNotice] = useState<string | null>(null);
  const [plateDecodedFields, setPlateDecodedFields] = useState<PlateDecodeFields | null>(null);
  const confirmGate = useConfirmGate();

  // Defaults ON for a new vehicle — matches VehicleForm.tsx's own
  // `useState(!vehicle)`.
  const [catalogMode, setCatalogMode] = useState(true);
  const [makeId, setMakeId] = useState<number | null>(null);
  const [modelId, setModelId] = useState<number | null>(null);
  const [trimIsManual, setTrimIsManual] = useState(false);

  const tenantSettingsQuery = useQuery({ queryKey: ["tenant-settings"], queryFn: tenantSettingsApi.get });
  const vehicleImageEnabled = tenantSettingsQuery.data?.settings.vehicleImageEnabled ?? false;

  const makesQuery = useQuery({ queryKey: ["vehicle-reference", "makes"], queryFn: vehicleReferenceApi.listMakes, enabled: catalogMode });
  const modelsQuery = useQuery({
    queryKey: ["vehicle-reference", "models", makeId],
    queryFn: () => vehicleReferenceApi.listModels(makeId!),
    enabled: catalogMode && makeId != null,
  });
  const yearsQuery = useQuery({
    queryKey: ["vehicle-reference", "years", modelId],
    queryFn: () => vehicleReferenceApi.listYears(modelId!),
    enabled: catalogMode && modelId != null,
  });
  const yearNumber = Number(modelYear);
  const trimsQuery = useQuery({
    queryKey: ["vehicle-reference", "trims", modelId, modelYear],
    queryFn: () => vehicleReferenceApi.listTrims(modelId!, yearNumber),
    enabled: catalogMode && modelId != null && !!modelYear && Number.isInteger(yearNumber),
  });
  const trimHasOptions = (trimsQuery.data?.trims.length ?? 0) > 0;

  async function switchToCatalogMode(): Promise<void> {
    setCatalogMode(true);
    setTrimIsManual(false);

    const typedMake = make.trim();
    const typedModel = model.trim();
    const typedYear = modelYear.trim();
    const typedTrim = trim.trim();

    function giveUp(from: "make" | "model" | "year" | "trim"): void {
      if (from === "make") {
        setMakeId(null);
        setModelId(null);
        setMake("");
        setModel("");
        setModelYear("");
        setTrim("");
      } else if (from === "model") {
        setModelId(null);
        setModel("");
        setModelYear("");
        setTrim("");
      } else if (from === "year") {
        setModelYear("");
        setTrim("");
      } else {
        setTrim("");
      }
    }

    if (!typedMake) return giveUp("make");
    try {
      const makes = await queryClient.fetchQuery({ queryKey: ["vehicle-reference", "makes"], queryFn: vehicleReferenceApi.listMakes });
      const matchedMake = makes.makes.find((m) => m.name.toLowerCase() === typedMake.toLowerCase());
      if (!matchedMake) return giveUp("make");
      setMakeId(matchedMake.id);
      setMake(matchedMake.name);

      if (!typedModel) return giveUp("model");
      const models = await queryClient.fetchQuery({
        queryKey: ["vehicle-reference", "models", matchedMake.id],
        queryFn: () => vehicleReferenceApi.listModels(matchedMake.id),
      });
      const matchedModel = models.models.find((m) => m.name.toLowerCase() === typedModel.toLowerCase());
      if (!matchedModel) return giveUp("model");
      setModelId(matchedModel.id);
      setModel(matchedModel.name);

      const yearNum = Number(typedYear);
      if (!typedYear || !Number.isInteger(yearNum)) return giveUp("year");
      const years = await queryClient.fetchQuery({
        queryKey: ["vehicle-reference", "years", matchedModel.id],
        queryFn: () => vehicleReferenceApi.listYears(matchedModel.id),
      });
      if (!years.years.some((y) => y.modelYear === yearNum)) return giveUp("year");

      if (!typedTrim) return giveUp("trim");
      const trims = await queryClient.fetchQuery({
        queryKey: ["vehicle-reference", "trims", matchedModel.id, typedYear],
        queryFn: () => vehicleReferenceApi.listTrims(matchedModel.id, yearNum),
      });
      const matchedTrim = trims.trims.find((t) => t.name.toLowerCase() === typedTrim.toLowerCase());
      if (matchedTrim) {
        setTrim(matchedTrim.name);
      } else if (trims.trims.length > 0) {
        setTrimIsManual(true);
      } else {
        setTrim("");
      }
    } catch {
      giveUp("make");
    }
  }

  function selectMake(raw: string): void {
    if (raw === OTHER) {
      setCatalogMode(false);
      return;
    }
    const idNum = Number(raw);
    const name = makesQuery.data?.makes.find((m) => m.id === idNum)?.name ?? "";
    setMakeId(idNum);
    setModelId(null);
    setTrimIsManual(false);
    setMake(name);
    setModel("");
    setTrim("");
    setModelYear("");
  }

  function selectModel(raw: string): void {
    const idNum = Number(raw);
    const name = modelsQuery.data?.models.find((m) => m.id === idNum)?.name ?? "";
    setModelId(idNum);
    setTrimIsManual(false);
    setModel(name);
    setTrim("");
    setModelYear("");
  }

  function selectYear(raw: string): void {
    setTrimIsManual(false);
    setModelYear(raw);
    setTrim("");
  }

  function selectTrim(raw: string): void {
    if (raw === OTHER) {
      setTrimIsManual(true);
      setTrim("");
      return;
    }
    setTrim(raw);
  }

  async function decodeVin(fillBlanks: boolean): Promise<void> {
    const vinValue = vin.trim();
    if (!vinValue) return;
    setDecodeNotice(null);
    setDecoding(true);
    try {
      const result = await vehiclesApi.decodeVin(vinValue);
      if (result.status === "unavailable") {
        setDecodedFields(null);
        if (fillBlanks) setDecodeNotice("VIN lookup is unavailable right now — you can still enter the vehicle's details by hand.");
        return;
      }
      const { fields } = result;
      setDecodedFields(fields);
      if (fillBlanks) {
        setCatalogMode(false);
        setMakeId(null);
        setModelId(null);
        setModelYear((v) => v || (fields.modelYear ? String(fields.modelYear) : v));
        setMake((v) => v || fields.make || v);
        setModel((v) => v || fields.model || v);
        setTrim((v) => v || fields.trim || v);
        setEngineDescription((v) => v || composeEngineDescription(fields) || v);
        setDecodeNotice(result.cached ? "Loaded from a previous lookup of this VIN." : "Decoded successfully.");
      }
    } catch {
      setDecodedFields(null);
      if (fillBlanks) setDecodeNotice("VIN lookup failed — you can still enter the vehicle's details by hand.");
    } finally {
      setDecoding(false);
    }
  }

  async function decodePlate(fillBlanks: boolean): Promise<void> {
    const plate = licensePlate.trim();
    const state = plateState.trim();
    if (!plate || !state) return;
    setPlateDecodeNotice(null);
    setDecodingPlate(true);
    try {
      const result = await vehiclesApi.decodePlate(plate, state);
      if (result.status === "not_configured") {
        setPlateDecodedFields(null);
        if (fillBlanks) setPlateDecodeNotice("License plate lookup isn't set up yet — you can still enter the vehicle's details by hand.");
        return;
      }
      if (result.status === "unavailable") {
        setPlateDecodedFields(null);
        if (fillBlanks) setPlateDecodeNotice("License plate lookup is unavailable right now — you can still enter the vehicle's details by hand.");
        return;
      }
      const { fields } = result;
      setPlateDecodedFields(fields);
      if (fillBlanks) {
        setCatalogMode(false);
        setMakeId(null);
        setModelId(null);
        setLicensePlateState(state);
        setVin((v) => v || fields.vin || v);
        setModelYear((v) => v || (fields.modelYear ? String(fields.modelYear) : v));
        setMake((v) => v || fields.make || v);
        setModel((v) => v || fields.model || v);
        setTrim((v) => v || fields.trim || v);
        setEngineDescription((v) => v || fields.engineDescription || v);
        setPlateDecodeNotice(result.cached ? "Loaded from a previous lookup of this plate." : "Decoded successfully.");
      }
    } catch {
      setPlateDecodedFields(null);
      if (fillBlanks) setPlateDecodeNotice("License plate lookup failed — you can still enter the vehicle's details by hand.");
    } finally {
      setDecodingPlate(false);
    }
  }

  async function save(): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      const { vehicle } = await vehiclesApi.create({
        customerId: id,
        entryMethod: "manual",
        vin: vin.trim() || null,
        modelYear: modelYear ? Number(modelYear) : null,
        make: make.trim() || null,
        model: model.trim() || null,
        trim: trim.trim() || null,
        engineDescription: engineDescription.trim() || null,
        licensePlate: licensePlate.trim() || null,
        licensePlateState: licensePlateState.trim() || null,
        imageUrl: imageUrl.trim() || null,
        lastOdometer: lastOdometer ? Number(lastOdometer) : null,
      });
      navigate(fromWorkOrder ? `/work-orders/new?customerId=${id}&vehicleId=${vehicle.id}` : `/customers/${customerNumber}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save this vehicle. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (isLoading) return <Spinner label="Loading customer…" />;
  if (loadError || !data) return <ModernErrorBanner message="Could not load this customer." />;

  return (
    <div className="mx-auto max-w-xl">
      <ModernBreadcrumbs
        items={
          fromWorkOrder
            ? [{ label: "New Work Order", to: backToWorkOrder }, { label: "Add Vehicle" }]
            : [
                { label: "Customers", to: "/customers" },
                { label: data.customer.displayName, to: `/customers/${customerNumber}` },
                { label: "Add Vehicle" },
              ]
        }
      />
      <h1 className="m-0 mb-4 text-[27px] font-bold text-[#071a33]">Add a Vehicle</h1>

      <ModernCard>
        <div className="mb-5 rounded-[10px] bg-[#e6f1f5] px-3.5 py-3">
          <div className="text-[12.5px] text-[#4a5361]">Customer</div>
          <div className="text-[17px] font-semibold text-[#071a33]">{data.customer.displayName}</div>
        </div>

        <ModernField label="VIN" htmlFor="vin">
          <div className="flex gap-2">
            <ModernInput id="vin" value={vin} onChange={(e) => setVin(e.target.value.toUpperCase())} />
            <ModernButton type="button" variant="secondary" className="whitespace-nowrap" onClick={() => void decodeVin(true)} disabled={decoding || !vin.trim()}>
              {decoding ? "Looking up…" : "Decode VIN"}
            </ModernButton>
          </div>
          <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">Optional — decode it to fill in the details below automatically.</p>
          {decodeNotice && <p className="mt-1 text-[13px] text-[#4a5361]">{decodeNotice}</p>}
        </ModernField>

        {decodedFields &&
          (decodedFields.modelYear || decodedFields.make || decodedFields.model || VIN_FIELD_LABELS.some(([key]) => decodedFields[key])) && (
            <div className="mb-5 rounded-[10px] border border-dashed border-[#e2e7ee] px-3.5 py-3">
              <h3 className="mb-2 text-[14px] font-bold text-[#071a33]">From the VIN</h3>
              <dl className="grid grid-cols-2 gap-y-1 text-[13px]">
                {decodedFields.modelYear && (
                  <div className="contents">
                    <dt className="text-[#6b7a8f]">Year</dt>
                    <dd className="text-right text-[#101b2d]">{decodedFields.modelYear}</dd>
                  </div>
                )}
                {decodedFields.make && (
                  <div className="contents">
                    <dt className="text-[#6b7a8f]">Make</dt>
                    <dd className="text-right text-[#101b2d]">{decodedFields.make}</dd>
                  </div>
                )}
                {decodedFields.model && (
                  <div className="contents">
                    <dt className="text-[#6b7a8f]">Model</dt>
                    <dd className="text-right text-[#101b2d]">{decodedFields.model}</dd>
                  </div>
                )}
                {VIN_FIELD_LABELS.filter(([key]) => decodedFields[key]).map(([key, label]) => (
                  <div key={key} className="contents">
                    <dt className="text-[#6b7a8f]">{label}</dt>
                    <dd className="text-right text-[#101b2d]">{String(decodedFields[key])}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            confirmGate.request(() => void save());
          }}
        >
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[14px] font-semibold text-[#071a33]">Make / Model / Year / Trim</span>
            <button
              type="button"
              className="text-[13px] text-[#0f6d86] underline"
              onClick={() => (catalogMode ? setCatalogMode(false) : void switchToCatalogMode())}
            >
              {catalogMode ? "Type it in manually instead" : "Use the Make/Model/Year/Trim picker instead"}
            </button>
          </div>

          {catalogMode ? (
            <>
              <div className="mb-3.5 grid grid-cols-2 gap-3.5">
                <ModernField label="Make" htmlFor="make">
                  <ModernSelect id="make" value={makeId ?? ""} onChange={(e) => selectMake(e.target.value)}>
                    <option value="" disabled>
                      Choose…
                    </option>
                    {makesQuery.data?.makes.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                    <option value={OTHER}>Other — type it in</option>
                  </ModernSelect>
                </ModernField>
                <ModernField label="Model" htmlFor="model">
                  <ModernSelect id="model" value={modelId ?? ""} onChange={(e) => selectModel(e.target.value)} disabled={makeId == null || modelsQuery.isLoading}>
                    <option value="" disabled>
                      Choose…
                    </option>
                    {modelsQuery.data?.models.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </ModernSelect>
                  {makeId == null && <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">Choose a make first</p>}
                </ModernField>
              </div>
              <div className="mb-3.5 grid grid-cols-2 gap-3.5">
                <ModernField label="Year" htmlFor="year">
                  <ModernSelect id="year" value={modelYear} onChange={(e) => selectYear(e.target.value)} disabled={modelId == null || yearsQuery.isLoading}>
                    <option value="" disabled>
                      Choose…
                    </option>
                    {yearsQuery.data?.years.map((y) => (
                      <option key={y.modelYear} value={y.modelYear}>
                        {y.modelYear}
                      </option>
                    ))}
                  </ModernSelect>
                  {modelId == null && <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">Choose a model first</p>}
                </ModernField>
                <ModernField label="Trim" htmlFor="trim">
                  {trimHasOptions && !trimIsManual ? (
                    <ModernSelect id="trim" value={trim} onChange={(e) => selectTrim(e.target.value)} disabled={!modelYear || trimsQuery.isLoading}>
                      <option value="">Choose…</option>
                      {trimsQuery.data?.trims.map((t) => (
                        <option key={t.id} value={t.name}>
                          {t.name}
                        </option>
                      ))}
                      <option value={OTHER}>Other — type it in</option>
                    </ModernSelect>
                  ) : (
                    <>
                      <ModernInput id="trim" placeholder="350" value={trim} onChange={(e) => setTrim(e.target.value)} disabled={!modelYear} />
                      {trimHasOptions && (
                        <button type="button" className="mt-1 text-[13px] text-[#0f6d86] underline" onClick={() => setTrimIsManual(false)}>
                          Choose from the list instead
                        </button>
                      )}
                    </>
                  )}
                  {!modelYear ? (
                    <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">Choose a year first</p>
                  ) : (
                    !trimHasOptions && <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">No trims on file — type it in</p>
                  )}
                </ModernField>
              </div>
            </>
          ) : (
            <>
              <div className="mb-3.5 grid grid-cols-2 gap-3.5">
                <ModernField label="Make" htmlFor="make">
                  <ModernInput id="make" placeholder="Lexus" value={make} onChange={(e) => setMake(e.target.value)} />
                </ModernField>
                <ModernField label="Model" htmlFor="model">
                  <ModernInput id="model" placeholder="ES" value={model} onChange={(e) => setModel(e.target.value)} />
                </ModernField>
              </div>
              <div className="mb-3.5 grid grid-cols-2 gap-3.5">
                <ModernField label="Year" htmlFor="year">
                  <ModernInput id="year" inputMode="numeric" placeholder="2008" value={modelYear} onChange={(e) => setModelYear(e.target.value)} />
                </ModernField>
                <ModernField label="Trim" htmlFor="trim">
                  <ModernInput id="trim" placeholder="350" value={trim} onChange={(e) => setTrim(e.target.value)} />
                </ModernField>
              </div>
            </>
          )}

          <ModernField label="Engine" htmlFor="engine">
            <ModernInput id="engine" value={engineDescription} onChange={(e) => setEngineDescription(e.target.value)} />
            <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">e.g. &quot;3.5L, V6&quot;</p>
          </ModernField>

          {PLATE_DECODER_ENABLED ? (
            <>
              <ModernField label="License Plate" htmlFor="plate">
                <div className="flex gap-2">
                  <ModernInput
                    id="plate"
                    className="flex-1"
                    value={licensePlate}
                    onChange={(e) => setLicensePlate(e.target.value.toUpperCase())}
                  />
                  <ModernSelect aria-label="License plate state" className="!w-28" value={plateState} onChange={(e) => setPlateState(e.target.value)}>
                    {US_STATE_CODES.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </ModernSelect>
                  <ModernButton
                    type="button"
                    variant="secondary"
                    className="whitespace-nowrap"
                    onClick={() => void decodePlate(true)}
                    disabled={decodingPlate || !licensePlate.trim() || !plateState}
                  >
                    {decodingPlate ? "Looking up…" : "Decode Plate"}
                  </ModernButton>
                </div>
                <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">
                  Add a state and decode to fill in the details above too — an additional option alongside the VIN decoder.
                </p>
                {plateDecodeNotice && <p className="mt-1 text-[13px] text-[#4a5361]">{plateDecodeNotice}</p>}
              </ModernField>

              {plateDecodedFields &&
                (plateDecodedFields.vin ||
                  plateDecodedFields.modelYear ||
                  plateDecodedFields.make ||
                  plateDecodedFields.model ||
                  PLATE_FIELD_LABELS.some(([key]) => plateDecodedFields[key])) && (
                  <div className="mb-5 rounded-[10px] border border-dashed border-[#e2e7ee] px-3.5 py-3">
                    <h3 className="mb-2 text-[14px] font-bold text-[#071a33]">From the Plate</h3>
                    <dl className="grid grid-cols-2 gap-y-1 text-[13px]">
                      {plateDecodedFields.vin && (
                        <div className="contents">
                          <dt className="text-[#6b7a8f]">VIN</dt>
                          <dd className="text-right text-[#101b2d]">{plateDecodedFields.vin}</dd>
                        </div>
                      )}
                      {plateDecodedFields.modelYear && (
                        <div className="contents">
                          <dt className="text-[#6b7a8f]">Year</dt>
                          <dd className="text-right text-[#101b2d]">{plateDecodedFields.modelYear}</dd>
                        </div>
                      )}
                      {plateDecodedFields.make && (
                        <div className="contents">
                          <dt className="text-[#6b7a8f]">Make</dt>
                          <dd className="text-right text-[#101b2d]">{plateDecodedFields.make}</dd>
                        </div>
                      )}
                      {plateDecodedFields.model && (
                        <div className="contents">
                          <dt className="text-[#6b7a8f]">Model</dt>
                          <dd className="text-right text-[#101b2d]">{plateDecodedFields.model}</dd>
                        </div>
                      )}
                      {PLATE_FIELD_LABELS.filter(([key]) => plateDecodedFields[key]).map(([key, label]) => (
                        <div key={key} className="contents">
                          <dt className="text-[#6b7a8f]">{label}</dt>
                          <dd className="text-right text-[#101b2d]">{String(plateDecodedFields[key])}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}
            </>
          ) : (
            <ModernField label="License Plate" htmlFor="plate">
              <ModernInput id="plate" value={licensePlate} onChange={(e) => setLicensePlate(e.target.value.toUpperCase())} />
            </ModernField>
          )}

          {vehicleImageEnabled ? (
            <div className="grid grid-cols-2 gap-3.5">
              <ModernField label="Current Odometer" htmlFor="odometer">
                <ModernInput id="odometer" inputMode="numeric" value={lastOdometer} onChange={(e) => setLastOdometer(e.target.value)} />
              </ModernField>
              <ModernField label="Vehicle Image URL" htmlFor="image-url">
                <ModernInput id="image-url" placeholder="https://…" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} />
                <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">Shown next to this vehicle on the customer page.</p>
              </ModernField>
            </div>
          ) : (
            <ModernField label="Current Odometer" htmlFor="odometer">
              <ModernInput id="odometer" inputMode="numeric" value={lastOdometer} onChange={(e) => setLastOdometer(e.target.value)} />
            </ModernField>
          )}

          {error && <ModernErrorBanner message={error} />}

          <div className="flex gap-2.5">
            <ModernButton type="submit" disabled={submitting}>
              {submitting ? "Saving…" : "Save Vehicle"}
            </ModernButton>
            <ModernButton
              type="button"
              variant="secondary"
              onClick={() => (fromWorkOrder ? navigate(backToWorkOrder) : navigate(-1))}
            >
              Cancel
            </ModernButton>
          </div>
        </form>
      </ModernCard>

      <ModernConfirmDialog {...confirmGate.dialogProps} message="Save this new vehicle?" />
    </div>
  );
}
